<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairProductCard } from '@/ui/patterns/FairProductCard'
import { FairProgressSpinner } from '@/ui/primitives/FairProgressSpinner'
import { usePopularTodayQuery } from '@/queries/recommendations'
import { useTokenStore } from '@/features/auth/store'
import { useFavorites } from '@/features/favorites/store/favorites'
import { useDislikeProductMutation } from '@/mutations/product/useDislikeProductMutation'
import { useToggleFavoriteMutation } from '@/mutations/favorites/useToggleFavoriteMutation'
import { useVariantCompareStore } from '@/features/compare/store'
import type { FairVariantCompareItem } from '@/ui/tokens/fair-variant-compare.data'
import { useRoute } from 'vue-router'
import {
  FAIR_HOME_POPULAR_TODAY_DEFAULT_AUDIENCE,
  FAIR_HOME_POPULAR_TODAY_LCP_PRIORITY_COUNT,
  resolveFairHomePopularToday,
  type FairHomePopularTodayCard,
  type FairHomePopularTodayRow,
} from './fair-home-popular-today.data'
import './fair-home-popular-today.css'

const { t, locale } = useI18n()
const route = useRoute()
const tokenStore = useTokenStore()
const favorites = useFavorites()
const compareStore = useVariantCompareStore()
const dislikeMutation = useDislikeProductMutation()
const favoriteMutation = useToggleFavoriteMutation()

// The URL is where the audience lives on this page, and the query key carries
// it — a tab switch must refetch, not re-serve the previous tab's block.
const audience = computed(() => {
  const value = route.query.audience
  const raw = Array.isArray(value) ? value[0] : value
  return (raw ?? FAIR_HOME_POPULAR_TODAY_DEFAULT_AUDIENCE) as string
})

const signedIn = computed(() => tokenStore.isAuthenticated())
const query = usePopularTodayQuery(audience, signedIn)

const removedIds = ref<Set<number>>(new Set())
const localFavorites = reactive<Record<number, boolean>>({})

const rows = computed<FairHomePopularTodayRow[]>(() =>
  resolveFairHomePopularToday(query.data.value, locale.value)
    .map((row) => ({
      ...row,
      cards: row.cards.filter((card) => !removedIds.value.has(card.variantId)),
    }))
    .filter((row) => row.cards.length > 0),
)

const isLoading = computed(
  () => query.isLoading.value && query.data.value == null,
)

// The whole section disappears when the block says nothing. It carries no demo
// fallback on purpose: one of its rows is the shopper's own taste, and a
// placeholder under that heading would claim a profile nobody read.
const hasRows = computed(() => rows.value.length > 0)

// THE HEART READS THE FAVOURITES STORE, not the payload. This block's segments
// are cached per tab and month and shared across shoppers, so they cannot carry
// a per-user flag without breaking that cache — and a heart that renders empty
// on a product she has already saved is a lie the old grid did not tell.
function isFavorite(card: FairHomePopularTodayCard): boolean {
  const local = localFavorites[card.variantId]
  if (local !== undefined) return local
  return favorites.arrayOfFavoritesProductVariants?.includes(card.variantId) ?? false
}

async function handleFavorite(card: FairHomePopularTodayCard) {
  const current = isFavorite(card)
  localFavorites[card.variantId] = !current
  try {
    // the mutation takes the CURRENT state and toggles it on the server
    await favoriteMutation.mutateAsync({
      variantId: card.variantId,
      isFavorite: current,
    })
  } catch {
    localFavorites[card.variantId] = current
  }
}

async function handleDislike(card: FairHomePopularTodayCard) {
  try {
    await dislikeMutation.mutateAsync({ variantId: card.variantId })
    removedIds.value = new Set(removedIds.value).add(card.variantId)
  } catch {
    // toast handled by the mutation; the card stays rather than vanishing on a
    // failure the shopper cannot see
  }
}

function isCompared(card: FairHomePopularTodayCard): boolean {
  return compareStore.has(card.variantId)
}

function handleCompare(card: FairHomePopularTodayCard) {
  const item: FairVariantCompareItem = {
    productId: card.id,
    variantId: card.variantId,
    brand: card.brand,
    title: card.title,
    price: card.price,
    imageUrl: card.images[0],
  }
  compareStore.toggle(item)
}

function cardTo(card: FairHomePopularTodayCard) {
  return {
    name: 'product' as const,
    params: {
      productId: String(card.id),
      productVariantId: String(card.variantId),
    },
  }
}

function isPriority(rowIndex: number, cardIndex: number): boolean {
  return rowIndex === 0 && cardIndex < FAIR_HOME_POPULAR_TODAY_LCP_PRIORITY_COUNT
}
</script>

<template>
  <section
    v-if="isLoading || hasRows"
    class="fair-home-popular-today"
    :aria-busy="isLoading"
  >
    <h2 class="fair-home-popular-today__section-title">
      {{ t('fair_home.products.title') }}
    </h2>

    <div
      v-if="isLoading"
      class="fair-home-popular-today__loading"
    >
      <FairProgressSpinner />
    </div>

    <div
      v-else
      class="fair-home-popular-today__rows"
    >
      <div
        v-for="(row, rowIndex) in rows"
        :key="row.id"
        class="fair-home-popular-today__row"
      >
        <div class="fair-home-popular-today__heading">
          <h3 class="fair-home-popular-today__title">
            {{ row.title }}
          </h3>
          <span
            v-if="row.personal"
            class="fair-home-popular-today__badge"
          >
            {{ t('fair_home.popular_today.personal') }}
          </span>
        </div>

        <div class="fair-home-popular-today__track">
          <div
            v-for="(card, cardIndex) in row.cards"
            :key="`${row.id}-${card.variantId}`"
            class="fair-home-popular-today__cell"
          >
            <FairProductCard
              :brand="card.brand"
              :title="card.title"
              :price="card.price"
              :images="card.images"
              orientation="vertical"
              :from-label="t('fair_home.products.from')"
              :favorite="isFavorite(card)"
              :compared="isCompared(card)"
              :to="cardTo(card)"
              :priority="isPriority(rowIndex, cardIndex)"
              @dislike="handleDislike(card)"
              @favorite="handleFavorite(card)"
              @compare="handleCompare(card)"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
